import React from 'react';
import * as Yup from 'yup';
import { format, isValid, parseISO } from 'date-fns';
import { Box, Button, IconButton, styled, Tooltip, Typography } from '@mui/material';
import { FieldArray, FieldArrayRenderProps, Form, getIn, useField, useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { MuiForm } from 'lib/Form';
import { CheckboxFormField } from 'lib/form/fields/CheckboxFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { DatePickerFormField } from 'lib/form/fields/DatePickerFormField';
import { MuiModal } from 'ui/MuiModal';
import {
    LocationDayOpeningHours,
    LocationOverrideOpeningHours
} from 'components/location/models/LocationModel';
import {
    createEmptyRange,
    DateOverrideEntry,
    getRangeOrderIssues,
    parseDateEntry,
    serializeRanges,
    TimeRangeValue
} from '../../locationOpeningHoursHelpers';
import { CopyTargetItem, CopyTimesConfigurationModal } from './CopyTimesConfigurationModal';

interface LocationDateOverrideFormValues {
    name: string;
    dates: DateOverrideEntry[];
}

interface FormProps {
    dateOverride?: LocationOverrideOpeningHours | null;
    onClose: () => void;
    onSave: (dateOverride: LocationOverrideOpeningHours) => void;
}

function createEmptyDateEntry(): DateOverrideEntry {
    return { date: null, closed: false, ranges: [createEmptyRange()] };
}

// API stores the date as a YYYY-MM-DD calendar string with no timezone, so we
// build the key locally and never go through UTC midnight.
function formatDateKey(date: Date): string {
    return format(date, 'yyyy-MM-dd');
}

// "00:00" is midnight at the end of the day, i.e. 12am the following day, so a
// slot crosses overnight when to === "00:00" or to < from. Both fields required.
const rangeSchema = Yup.object({
    from: Yup.string().required('Enter an opening time'),
    to: Yup.string().required('Enter a closing time')
});

const dateEntrySchema = Yup.object({
    date: Yup.date().typeError('Enter a valid date.'),
    closed: Yup.boolean(),
    ranges: Yup.array().when('closed', {
        is: (closed: boolean) => !closed,
        then: schema =>
            schema
                .of(rangeSchema)
                .min(1, 'Add at least one time range')
                .test('split-hours-order', function splitHoursOrder(ranges) {
                    const list = (ranges ?? []) as TimeRangeValue[];
                    if (list.length < 2) {
                        return true;
                    }
                    const issues = getRangeOrderIssues(list);
                    if (issues.length === 0) {
                        return true;
                    }
                    return new Yup.ValidationError(
                        issues.map(issue =>
                            this.createError({
                                path: `${this.path}.${issue.index}.${issue.field}`,
                                message: issue.message
                            })
                        )
                    );
                })
    })
});

const validationSchema = Yup.object({
    name: Yup.string().trim().required('Enter a name'),
    dates: Yup.array()
        .of(dateEntrySchema)
        .min(1, 'Add at least one date')
        // Flags every occurrence of a duplicate date so the user can see both
        // rows that clash, not just the second one.
        .test('unique-dates', function uniqueDates(dates) {
            const list = (dates ?? []) as DateOverrideEntry[];
            const indicesByKey = new Map<string, number[]>();
            list.forEach((entry, index) => {
                if (!entry.date || !isValid(entry.date)) {
                    return;
                }
                const key = formatDateKey(entry.date);
                const existing = indicesByKey.get(key);
                if (existing) {
                    existing.push(index);
                } else {
                    indicesByKey.set(key, [index]);
                }
            });
            const errors: Yup.ValidationError[] = [];
            indicesByKey.forEach(indices => {
                if (indices.length > 1) {
                    indices.forEach(index => {
                        errors.push(
                            this.createError({
                                path: `${this.path}.${index}.date`,
                                message: 'This date has already been added'
                            })
                        );
                    });
                }
            });
            if (errors.length === 0) {
                return true;
            }
            return new Yup.ValidationError(errors);
        })
});

interface RangeRowProps {
    namePrefix: string;
    index: number;
    rangeCount: number;
    arrayHelpers: FieldArrayRenderProps;
    isLast: boolean;
}

const RangeRow: React.FC<RangeRowProps> = ({ namePrefix, index, rangeCount, arrayHelpers, isLast }) => {
    const [fromField, fromMeta] = useField<string>(`${namePrefix}.${index}.from`);
    const [toField, toMeta] = useField<string>(`${namePrefix}.${index}.to`);

    const handleAdd = React.useCallback(() => {
        arrayHelpers.push(createEmptyRange());
    }, [arrayHelpers]);

    const handleRemove = React.useCallback(() => {
        arrayHelpers.remove(index);
    }, [arrayHelpers, index]);

    const errorMessage =
        (fromMeta.touched && fromMeta.error) || (toMeta.touched && toMeta.error) || undefined;

    const crossesMidnight =
        !!fromField.value &&
        !!toField.value &&
        (toField.value === '00:00' || toField.value < fromField.value);

    return (
        <Box marginBottom={1}>
            <Box display="flex" alignItems="center" gap={1}>
                <Box display="flex" maxWidth={400} flexDirection="row" gap={1} flexGrow={1}>
                    <TextFormField
                        name={`${namePrefix}.${index}.from`}
                        label="From"
                        type="time"
                        size="small"
                        shrink
                        showHelperText={false}
                        sx={{ flexGrow: 1 }}
                    />
                    <span>-</span>
                    <TextFormField
                        name={`${namePrefix}.${index}.to`}
                        label="To"
                        type="time"
                        size="small"
                        shrink
                        showHelperText={false}
                        sx={{ flexGrow: 1 }}
                    />
                </Box>
                <Tooltip title="Delete this time range">
                    <span>
                        <IconButton onClick={handleRemove} disabled={rangeCount <= 1} size="small">
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                {isLast && (
                    <Tooltip title="Add split time range">
                        <IconButton onClick={handleAdd} size="small">
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {crossesMidnight && (
                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                    Time crosses midnight, closing on the next day
                </Typography>
            )}
            {errorMessage && (
                <Typography variant="caption" sx={{ color: 'error.main' }}>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};

interface DateRangesEditorProps {
    dateIndex: number;
    arrayHelpers: FieldArrayRenderProps;
}

const DateRangesEditor: React.FC<DateRangesEditorProps> = ({ dateIndex, arrayHelpers }) => {
    const { values } = useFormikContext<LocationDateOverrideFormValues>();
    const entry = values.dates[dateIndex];
    const ranges = entry.ranges;
    const namePrefix = `dates.${dateIndex}.ranges`;

    return (
        <Box flex={1}>
            {!entry.closed &&
                ranges.map((_range, index) => (
                    <RangeRow
                        key={`date-${dateIndex}-range-${index}`}
                        namePrefix={namePrefix}
                        index={index}
                        rangeCount={ranges.length}
                        arrayHelpers={arrayHelpers}
                        isLast={index === ranges.length - 1}
                    />
                ))}
            <CheckboxFormField
                name={`dates.${dateIndex}.closed`}
                label={<Typography variant="caption">Closed</Typography>}
            />
        </Box>
    );
};

const StyledDateRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 0, 1, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': {
        borderBottom: 'none'
    }
}));

interface DateRowProps {
    index: number;
    onRemove: (index: number) => void;
}

const DateRow: React.FC<DateRowProps> = ({ index, onRemove }) => {
    const { values, errors, setFieldValue } = useFormikContext<LocationDateOverrideFormValues>();
    const { closed, ranges, date } = values.dates[index];
    const [openCopyModal, setOpenCopyModal] = React.useState(false);

    const isSourceInvalid =
        !date || closed || !!getIn(errors, `dates.${index}`) || ranges.every(r => !r.from && !r.to);

    const copyItems = React.useMemo<CopyTargetItem[]>(
        () =>
            values.dates
                .filter(entry => entry.date && isValid(entry.date))
                .map((entry, i) => ({
                    id: i,
                    label: format(entry.date, 'd MMM yyyy'),
                    closed: entry.closed
                }))
                .filter(item => item.id !== index),
        [values.dates, index]
    );

    const sourceLabel = React.useMemo(() => {
        if (!date || !isValid(date)) {
            return `Date ${index + 1}`;
        }
        return format(date, 'd MMM yyyy');
    }, [date, index]);

    const handleRemove = React.useCallback(() => {
        onRemove(index);
    }, [onRemove, index]);

    const handleOpenCopyModal = React.useCallback(() => {
        setOpenCopyModal(true);
    }, []);

    const handleCloseCopyModal = React.useCallback(() => {
        setOpenCopyModal(false);
    }, []);

    const handleCopy = React.useCallback(
        (ids: (string | number)[]) => {
            setOpenCopyModal(false);
            ids.forEach(id => {
                const targetIndex = id as number;
                setFieldValue(`dates.${targetIndex}.closed`, closed);
                setFieldValue(
                    `dates.${targetIndex}.ranges`,
                    closed ? [] : ranges.map(range => ({ ...range }))
                );
            });
        },
        [closed, ranges, setFieldValue]
    );

    return (
        <StyledDateRow>
            <Box display="flex" flexDirection="row" gap={1} alignItems="end">
                <DatePickerFormField name={`dates.${index}.date`} label="Date" />
                <Tooltip title="Delete this date">
                    <IconButton onClick={handleRemove}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Copy configuration to other dates">
                    <span>
                        <IconButton
                            onClick={handleOpenCopyModal}
                            disabled={isSourceInvalid || copyItems.length === 0}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <FieldArray name={`dates.${index}.ranges`}>
                {arrayHelpers => <DateRangesEditor dateIndex={index} arrayHelpers={arrayHelpers} />}
            </FieldArray>
            <CopyTimesConfigurationModal
                open={openCopyModal}
                sourceLabel={sourceLabel}
                items={copyItems}
                onClose={handleCloseCopyModal}
                onSubmit={handleCopy}
            />
        </StyledDateRow>
    );
};

interface DateListProps {
    dates: DateOverrideEntry[];
    arrayHelpers: FieldArrayRenderProps;
}

const DateList: React.FC<DateListProps> = ({ dates, arrayHelpers }) => {
    const handleAdd = React.useCallback(() => {
        arrayHelpers.push(createEmptyDateEntry());
    }, [arrayHelpers]);
    const handleRemove = React.useCallback(
        (index: number) => {
            arrayHelpers.remove(index);
        },
        [arrayHelpers]
    );

    return (
        <>
            <Box>
                {dates.map((_entry, index) => (
                    <DateRow key={`date-${index}`} index={index} onRemove={handleRemove} />
                ))}
            </Box>
            <Box mt={1}>
                <Button variant="text" size="small" color="primary" onClick={handleAdd}>
                    Add Date
                </Button>
            </Box>
        </>
    );
};

const LocationDateOverrideForm: React.FC<FormProps> = ({ dateOverride, onClose, onSave }) => {
    const { enqueueSnackbar } = useSnackbar();

    const initialValues = React.useMemo<LocationDateOverrideFormValues>(
        () => ({
            name: dateOverride?.name ?? '',
            // Sort dates ascending so the rows render in a predictable order
            // when editing an existing override.
            dates: dateOverride
                ? Object.entries(dateOverride.dates)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([iso, ranges]) => parseDateEntry(parseISO(iso), ranges))
                : [createEmptyDateEntry()]
        }),
        [dateOverride]
    );

    const handleSubmit = React.useCallback(
        (values: LocationDateOverrideFormValues) => {
            try {
                const dates = values.dates.reduce<Record<string, LocationDayOpeningHours[]>>((acc, entry) => {
                    if (!entry.date) {
                        return acc;
                    }
                    acc[formatDateKey(entry.date)] = entry.closed ? [] : serializeRanges(entry.ranges);
                    return acc;
                }, {});
                return onSave({
                    _id: dateOverride?._id,
                    name: values.name.trim(),
                    dates
                });
            } catch (_error) {
                enqueueSnackbar(
                    'Something went wrong saving these date overrides. Please check the dates and try again.',
                    {
                        variant: 'error'
                    }
                );
                return undefined;
            }
        },
        [dateOverride, onSave, enqueueSnackbar]
    );

    return (
        <MuiForm<LocationDateOverrideFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ submitForm, isSubmitting, values }) => (
                <Form>
                    <Box py={2}>
                        <TextFormField name="name" label="Name" placeholder="e.g. Easter Holiday" />
                    </Box>
                    <FieldArray name="dates">
                        {arrayHelpers => <DateList dates={values.dates} arrayHelpers={arrayHelpers} />}
                    </FieldArray>
                    <Box width="100%" marginTop={2} display="flex" justifyContent="flex-end" columnGap={1}>
                        <Button disabled={isSubmitting} variant="outlined" color="primary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            disabled={isSubmitting}
                            variant="contained"
                            color="primary"
                            onClick={submitForm}
                        >
                            Save
                        </Button>
                    </Box>
                </Form>
            )}
        </MuiForm>
    );
};

const StyledModalHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderColor: theme.palette.divider,
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1)
}));

interface IProps {
    open: boolean;
    onClose: () => void;
    dateOverride?: LocationOverrideOpeningHours | null;
    onSave: (dateOverride: LocationOverrideOpeningHours) => void;
}

export const LocationDateOverrideModal = ({ open, onClose, dateOverride, onSave }: IProps) => {
    const isEditing = !!dateOverride;
    const formTitle = isEditing ? 'Edit Date Override' : 'Add Date Override';
    return (
        <MuiModal
            open={open}
            onClose={onClose}
            paperMaxWidth={600}
            sx={{ '&&': { width: '100%' } }}
            disableBackdropClick
        >
            <>
                <StyledModalHeader>
                    <Typography variant="h6">
                        <b>{formTitle}</b>
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </StyledModalHeader>
                <LocationDateOverrideForm dateOverride={dateOverride} onClose={onClose} onSave={onSave} />
            </>
        </MuiModal>
    );
};
